import React, { useState } from "react";
import { Badge, Button, Alert, Spinner, Modal, Card } from "react-bootstrap";
import { useSubscription } from "../hooks/useSubscription";

/**
 * SubscriptionStatus Component
 * Issue #9: Frontend State Management
 *
 * Displays user's subscription status with actions
 */
const SubscriptionStatus = ({
  showDetails = false,
  showActions = true,
  compact = false,
  className = "",
}) => {
  const {
    subscription,
    loading,
    error,
    hasAccess,
    isAuthenticated,
    isAdmin,
    activateTrial,
    refreshSubscription,
    getTrialDaysRemaining,
    isInTrial,
    getSubscriptionStatusDisplay,
    getSubscriptionStatusColor,
    canActivateTrial,
    needsPayment,
    isGracePeriod,
    getAccessReason,
  } = useSubscription();

  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Don't show anything for unauthenticated users
  if (!isAuthenticated) {
    return null;
  }

  // Admin users - show admin badge
  if (isAdmin) {
    return (
      <Badge bg="primary" className={className}>
        <i className="fas fa-crown me-1"></i>
        Admin Access
      </Badge>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className={`d-flex align-items-center ${className}`}>
        <Spinner size="sm" className="me-2" />
        <span className="text-muted">Loading subscription...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="warning" className={`mb-0 py-2 ${className}`}>
        <small>
          <i className="fas fa-exclamation-triangle me-1"></i>
          Subscription Error
          {showActions && (
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2"
              onClick={() => refreshSubscription()}
            >
              Retry
            </Button>
          )}
        </small>
      </Alert>
    );
  }

  const handleActivateTrial = async () => {
    setActionLoading(true);
    try {
      const result = await activateTrial();
      if (result.success) {
        setShowModal(false);
      } else {
        console.error("Trial activation failed:", result.message);
      }
    } catch (error) {
      console.error("Trial activation error:", error);
    } finally {
      setActionLoading(false);
    }
  };

  // No subscription - show trial activation option
  if (!subscription && canActivateTrial()) {
    return (
      <div className={className}>
        {compact ? (
          <Button
            variant="success"
            size="sm"
            onClick={() => setShowModal(true)}
          >
            Start Free Trial
          </Button>
        ) : (
          <Alert variant="info" className="mb-0 py-2">
            <div className="d-flex justify-content-between align-items-center">
              <small>
                <i className="fas fa-gift me-1"></i>
                No active subscription
              </small>
              {showActions && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => setShowModal(true)}
                >
                  Start 3-Day Free Trial
                </Button>
              )}
            </div>
          </Alert>
        )}

        {/* Trial Activation Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Start Your Free Trial</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Card className="border-0">
              <Card.Body className="text-center">
                <div className="mb-3">
                  <i
                    className="fas fa-gift text-success"
                    style={{ fontSize: "3rem" }}
                  ></i>
                </div>
                <h5>3-Day Free Trial</h5>
                <p className="text-muted">
                  Get full access to all features for 3 days. No credit card
                  required.
                </p>
                <ul className="text-start">
                  <li>Access to all questions and worksheets</li>
                  <li>Create and share custom questions</li>
                  <li>Advanced filtering and search</li>
                  <li>Generate unlimited worksheets</li>
                </ul>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Maybe Later
            </Button>
            <Button
              variant="success"
              onClick={handleActivateTrial}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Activating...
                </>
              ) : (
                "Start Free Trial"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // Has subscription - show status
  if (subscription) {
    const statusColor = getSubscriptionStatusColor();
    const statusDisplay = getSubscriptionStatusDisplay();
    const trialDays = getTrialDaysRemaining();
    const inTrial = isInTrial();
    const grace = isGracePeriod();
    const payment = needsPayment();

    return (
      <div className={className}>
        {compact ? (
          <Badge bg={statusColor}>
            {inTrial && trialDays > 0 ? (
              <>
                <i className="fas fa-clock me-1"></i>
                Trial: {trialDays}d
              </>
            ) : (
              <>
                <i
                  className={`fas fa-${
                    statusColor === "success" ? "check" : "exclamation"
                  } me-1`}
                ></i>
                {statusDisplay}
              </>
            )}
          </Badge>
        ) : (
          <Alert variant={statusColor} className="mb-0 py-2">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small>
                  <strong>{statusDisplay}</strong>
                  {inTrial && trialDays > 0 && (
                    <span className="ms-2">
                      <i className="fas fa-clock me-1"></i>
                      {trialDays} day{trialDays !== 1 ? "s" : ""} remaining
                    </span>
                  )}
                  {grace && (
                    <span className="ms-2">
                      <i className="fas fa-hourglass-half me-1"></i>
                      Grace period
                    </span>
                  )}
                  {payment && (
                    <span className="ms-2">
                      <i className="fas fa-credit-card me-1"></i>
                      Payment required
                    </span>
                  )}
                </small>
                {showDetails && (
                  <div className="mt-1">
                    <small className="text-muted">
                      Access: {getAccessReason()}
                    </small>
                  </div>
                )}
              </div>

              {showActions && (
                <div>
                  {payment && (
                    <Button variant="warning" size="sm" className="me-2">
                      Update Payment
                    </Button>
                  )}
                  {inTrial && trialDays <= 1 && (
                    <Button variant="primary" size="sm">
                      Subscribe Now
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => refreshSubscription()}
                    className="ms-2"
                  >
                    <i className="fas fa-sync-alt"></i>
                  </Button>
                </div>
              )}
            </div>
          </Alert>
        )}
      </div>
    );
  }

  // Fallback - should not reach here normally
  return (
    <Badge bg="secondary" className={className}>
      <i className="fas fa-question me-1"></i>
      Unknown Status
    </Badge>
  );
};

export default SubscriptionStatus;
